import React, { useContext, useState } from 'react'

import { inViewport, WithAddedProps } from '../../__helpers/in-viewport'

import { Logger } from 'typescript-log'

import { ErrorBoundary } from '../../errors/ErrorBoundary/ErrorBoundary'
import { Button } from '../../buttons/Button/Button'
import { LoggerContext } from '../../diagnostics/LoggerContext'
import {
    PlayerInterface,
    VideoOptions,
    AutoplayOptions,
    GetVideoQueue,
    PlayerProvider,
} from '../../content/Video/PlayerInterface'
import { VideoDetails } from '@news-mono/web-common'
import { VideoSkin } from '../../content/Video/VideoSkin'
import { ConfigurationContext } from '@news-mono/web-common'
import { PlayerEvent, PagePosition } from '@news-mono/web-common'
import { LazySwmPlayerProvider } from './providers/LazySwmPlayerProvider'
import { useTheme } from '@emotion/react'
import { IsHydrating } from '@news-mono/web-common'
import { useScriptWithConsent, ScriptLoadResult } from '@news-mono/web-common'
import { ConsentLevel } from '@news-mono/web-common'
import { StyledVideoErrorBoundary } from './Video.styled'

/**
 * Use the player provider context to change the player provider used in the viceo component. This
 * is useful for passing in a test provider. This provider can be monitored to ensure that the player
 * component is working correctly
 */
export const playerProviderContext = React.createContext<
    (logger: Logger) => PlayerProvider
>((logger) => {
    // bc _should_ always be defined at this point in production,
    // but this will save the day if brightcove ever starts serving dodgy scripts
    // it also reduces noise on product tests in verbose mode
    const brightcoveGlobalExists = typeof bc !== 'undefined'

    if (!brightcoveGlobalExists) {
        logger.warn('Global brightcove object does not exist')
    }

    return brightcoveGlobalExists ? LazySwmPlayerProvider : () => null
})

// We don't actually care about the type of this
declare const bc: undefined | any

export interface VideoProps extends WithAddedProps {
    videoDetails: VideoDetails

    videoOptions: VideoOptions

    // todo do something with this
    pauseOverlay?: boolean

    // shows the video title bar
    showTitles: boolean

    /** false to disable autoplay */
    autoplayOptions: AutoplayOptions | false

    autoplayNextOptions: AutoplayOptions | false

    onEvent: (event: PlayerEvent) => void

    /**
     * defaults to 'standard'
     */

    getVideoQueue: GetVideoQueue

    pagePosition: PagePosition

    /**
     * The `playWhenReady` prop tells the video to try and play the video. You can use this prop to try autoplay the
     * video immediately, but its preferable to use the autoplay options for that instead. This prop should only be used
     * if you need to effect the playing of the video component from its the parent component.
     */
    playWhenReady?: boolean

    onUserInteraction: (e: PlayerEvent) => void

    showPlaylist?: boolean
    playButtonText?: string
}

export const Video = inViewport<VideoProps>((props) => {
    const theme = useTheme()
    const getCustomPlayerProvider = useContext(playerProviderContext)
    const logger = useContext(LoggerContext)
    const config = useContext(ConfigurationContext)
    const [result] = useScriptWithConsent({
        consentRequiredLevel: ConsentLevel.All,
        consentMet: {
            // from players.brightcove.net/4761797480001/g0iOzsBYC_default/index.min.js`
            // eslint-disable-next-line import/no-webpack-loader-syntax
            src: require('!!file-loader?esModule=false&outputPath=static/js!./player_4761797480001_g0iOzsBYC.min.js'),
        },
    })
    const hydrating = useContext(IsHydrating)
    // Use !!props.autoplayOptions instead of true to only load automatically when autoplay is on
    const [loadPlayer, setLoadPlayer] = useState(true)

    // TODO Handle script loading error
    if (hydrating || result !== ScriptLoadResult.Success || !loadPlayer) {
        return (
            <VideoSkin
                theme={theme}
                playerActions={{
                    playVideo: () => {
                        setLoadPlayer(true)
                    },
                    handleMuteToggle: () => {},
                    stopAutoplay: () => {},
                    closeStickyVideo: () => {},
                }}
                playerOptions={{
                    compactMode: props.videoOptions.compactMode,
                    playButtonText: props.playButtonText,
                }}
                playerState={{
                    adPlayed: false,
                    adPlaying: false,
                    canAutoplay: false,
                    currentState: { type: 'other' },
                    currentVideo: props.videoDetails,
                    currentVideoMetadata: undefined,
                    currentVideoPlaylistType: 'not-playlist-video',
                    inViewport: props.inViewport,
                    isFullscreen: false,
                    isMuted: false,
                    showInternalPlayButton: true,
                    showTitleOverlay: false,
                    showTopBar: false,
                }}
                videoElement={null}
                wrapperElProps={{
                    onMouseEnter: () => {},
                    onMouseLeave: () => {},
                    onMouseMove: () => {},
                    onTouchEnd: () => {},
                    onTouchStart: () => {},
                    innerRef: () => {},
                }}
            />
        )
    }

    return (
        <ErrorBoundary
            log={logger}
            renderError={(retry) => (
                <StyledVideoErrorBoundary>
                    <p>An error has occurred with this video.</p>
                    <Button onClick={() => retry()}>Click here to retry</Button>
                </StyledVideoErrorBoundary>
            )}
        >
            <PlayerInterface
                {...props}
                theme={theme}
                config={config}
                logger={logger}
                playerProvider={() => getCustomPlayerProvider(logger)}
                renderPlayer={(renderPlayerProps) => (
                    <VideoSkin {...renderPlayerProps} />
                )}
            />
        </ErrorBoundary>
    )
})
Video.displayName = 'Video'
